import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Download, Filter, RotateCcw, Search, ShieldAlert } from 'lucide-react';
import { Input } from 'components/ui/input';
import { MultiSelect } from 'components/ui/multi-select';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';
import { useParentKeyInput } from 'hooks/useParentKeyInput';
import { useTechniciansList } from 'hooks/useTechnicians';
import { technicianFullName } from 'pages/AdminTechnicians/csv';
import {
  REFUSAL_REASON_CODES,
  REQUEST_STATUSES,
  hasActiveFollowUpFilters,
  type FollowUpFilters,
  type InterventionRequestStatus,
  type RefusalReasonCode,
} from 'api/interventionRequestsApi';
import { refusalReasonLabel } from 'pages/AdminIssues/components/upcomingAssignment';
import { followUpStatusLabel } from '../vocabulary';

/**
 * The toolbar: the desk picker, one search box, the status chips, and everything else behind a
 * « Filtres » disclosure with its count — the shape `IssuesFiltersBar` settled on.
 *
 * **Status and the refusal reason are here and not in the sort** (D37). They are translated
 * labels: ordering « Accepté / En attente / Refusé » alphabetically answers no question, while
 * counting them is the whole reason D25 made the refusal a code.
 *
 * ⚠️ **No « envoyée par » picker.** The column and the API filter exist (D39), but nothing lists
 * the operators who have sent something, and a picker fed from the page on screen would offer a
 * different set on every page. The search box and the column cover the need until an option
 * source does; the backend filter stays, unused by this bar and costing nothing.
 */

const ADVANCED_ID = 'follow-up-advanced-filters';
const PARENT_HINT_ID = 'follow-up-parent-key-hint';

/** Collapse an empty multi-select to `undefined` so the filter clears entirely. */
const orUndefined = <T,>(values: T[]): T[] | undefined => (values.length ? values : undefined);

/** What the collapsed panel hides — and only that. The search and the status stay on screen. */
const ADVANCED_KEYS: (keyof FollowUpFilters)[] = [
  'technicianId',
  'refusalReasonCode',
  'outsidePerimeter',
  'parentKey',
  'sentFrom',
  'sentTo',
  'interventionFrom',
  'interventionTo',
];

const isSet = (value: unknown): boolean => (Array.isArray(value) ? value.length > 0 : !!value);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-[7px]">
    <label className="text-[12.5px] font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

const CONTROL =
  'inline-flex h-[38px] items-center gap-[7px] rounded-[9px] border bg-background px-[13px] text-[13px] font-medium';

interface FollowUpFiltersBarProps {
  filters: FollowUpFilters;
  onChange: (partial: Partial<FollowUpFilters>) => void;
  onReset: () => void;
  onExport: () => void;
  exporting: boolean;
  canExport: boolean;
  /** The desk picker, built by the page — it is the scope, not a filter (D40). */
  deskSelect: ReactNode;
  /** Split desks only (`splitsIntoChildren`). */
  showParentFilter?: boolean;
}

export const FollowUpFiltersBar = ({
  filters,
  onChange,
  onReset,
  onExport,
  exporting,
  canExport,
  deskSelect,
  showParentFilter = false,
}: FollowUpFiltersBarProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useDebouncedSearch(filters.search, (next) =>
    onChange({ search: next })
  );
  const [parentInput, setParentInput, parentMalformed] = useParentKeyInput(
    filters.parentKey,
    filters.serviceDeskId,
    (parentKey) => onChange({ parentKey })
  );

  /**
   * The pool, for the technician picker. One page big enough to hold it — the directory's own CSV
   * export reads it the same way — and `staleTime` on the hook keeps it from being refetched on
   * every keystroke elsewhere. Scoped to nothing: a technician is cross-desk by design.
   */
  const { data: pool } = useTechniciansList({
    orderBy: 'name',
    order: 'asc',
    page: 1,
    pageSize: 1000,
  });

  const technicianOptions = useMemo(
    () =>
      (pool?.technicians ?? []).map((technician) => ({
        value: String(technician.id),
        label: technicianFullName(technician),
      })),
    [pool]
  );

  const statusOptions = useMemo(
    () =>
      REQUEST_STATUSES.map((status) => ({
        value: status,
        label: followUpStatusLabel(t, status),
      })),
    [t]
  );

  const refusalOptions = useMemo(
    () => REFUSAL_REASON_CODES.map((code) => ({ value: code, label: refusalReasonLabel(t, code) })),
    [t]
  );

  // Derived, never stored: a count kept in state is a count that can lie about the panel.
  const advancedCount = ADVANCED_KEYS.filter((key) => isSet(filters[key])).length;
  // A malformed key is never committed, so the typed text alone also offers the reset.
  const showReset = hasActiveFollowUpFilters(filters) || !!parentInput.trim();

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2.5">
        {deskSelect}

        <div className="relative min-w-[240px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          {/* One box over the ticket key, the store and the technician: the ops team speaks in
              `LODPGP-1977` as much as in « Sephora Rivoli ». */}
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('Search by ticket, store or technician')}
            aria-label={t('Search by ticket, store or technician')}
            className="h-[38px] rounded-[9px] pl-9 text-[13.5px] md:text-[13.5px]"
          />
        </div>

        <MultiSelect
          options={statusOptions}
          selected={filters.status ?? []}
          onChange={(values) =>
            onChange({ status: orUndefined(values as InterventionRequestStatus[]) })
          }
          label={t('Status')}
          summary
          placeholder={t('Any status')}
          aria-label={t('Status')}
        />

        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          // Never point an ARIA relationship at an id behind a conditional render (R-027).
          aria-controls={open ? ADVANCED_ID : undefined}
          title={advancedCount > 0 ? t('{{count}} active filters', { count: advancedCount }) : undefined}
          className={`${CONTROL} ${open ? 'border-foreground/20' : 'border-input'}`}
        >
          <Filter className="h-[15px] w-[15px]" aria-hidden="true" />
          {t('Filters')}
          {/* Plain text inside the button, never aria-hidden: the count is part of its name. */}
          {advancedCount > 0 && (
            <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[6px] bg-foreground px-[5px] text-[11px] font-semibold text-background">
              {advancedCount}
            </span>
          )}
          <ChevronDown
            className={`h-[15px] w-[15px] text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {/* Nothing set, nothing to reset: an always-on button offers to undo an empty state. */}
        {showReset && (
          <button
            type="button"
            onClick={() => {
              setParentInput('');
              onReset();
            }}
            className={`${CONTROL} border-none bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground`}
          >
            <RotateCcw className="h-[15px] w-[15px]" aria-hidden="true" />
            {t('Reset')}
          </button>
        )}

        <button
          type="button"
          onClick={onExport}
          disabled={exporting || !canExport}
          className={`${CONTROL} ml-auto border-zinc-300 disabled:opacity-50 dark:border-zinc-700`}
        >
          <Download className="h-[15px] w-[15px]" aria-hidden="true" />
          {exporting ? t('Exporting…') : t('Export CSV')}
        </button>
      </div>

      {open && (
        <div
          id={ADVANCED_ID}
          role="group"
          aria-label={t('Advanced filters')}
          className="mt-[14px] grid grid-cols-1 gap-x-4 gap-y-[14px] border-t pt-4 md:grid-cols-2 lg:grid-cols-4"
        >
          <Field label={t('Technician')}>
            <MultiSelect
              options={technicianOptions}
              selected={(filters.technicianId ?? []).map(String)}
              onChange={(values) => onChange({ technicianId: orUndefined(values.map(Number)) })}
              placeholder={t('Any technician')}
              // `Field`'s label is a sibling, not a wrapper, and `MultiSelect` names its trigger
              // from this prop alone — without it the control has no accessible name.
              aria-label={t('Technician')}
              searchable
              disabled={technicianOptions.length === 0}
            />
          </Field>

          {showParentFilter && (
            <Field label={t('Parent ticket')}>
              <Input
                aria-label={t('Parent ticket')}
                placeholder="ABC-123"
                value={parentInput}
                onChange={(event) => setParentInput(event.target.value)}
                aria-invalid={parentMalformed}
                aria-describedby={parentMalformed ? PARENT_HINT_ID : undefined}
                className="h-[38px] rounded-[9px] text-[13px]"
              />
              {parentMalformed && (
                <p id={PARENT_HINT_ID} className="text-[12px] text-rose-600 dark:text-rose-400">
                  {t('Expected format: ABC-123')}
                </p>
              )}
            </Field>
          )}

          {/* The point of the code (D25): thirty refusals only tell you something once you can
              count them by reason. */}
          <Field label={t('Refusal reason')}>
            <MultiSelect
              options={refusalOptions}
              selected={filters.refusalReasonCode ?? []}
              onChange={(values) =>
                onChange({ refusalReasonCode: orUndefined(values as RefusalReasonCode[]) })
              }
              placeholder={t('Any reason')}
              aria-label={t('Refusal reason')}
            />
          </Field>

          <Field label={t('Sent on')}>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.sentFrom ?? ''}
                onChange={(event) => onChange({ sentFrom: event.target.value || undefined })}
                aria-label={t('Sent from')}
                className="h-[38px] rounded-[9px] text-[13px]"
              />
              <Input
                type="date"
                value={filters.sentTo ?? ''}
                onChange={(event) => onChange({ sentTo: event.target.value || undefined })}
                aria-label={t('Sent until')}
                className="h-[38px] rounded-[9px] text-[13px]"
              />
            </div>
          </Field>

          {/* The planning read: what is confirmed for this week. Without it the column is
              decorative. */}
          <Field label={t('Intervention date')}>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.interventionFrom ?? ''}
                onChange={(event) => onChange({ interventionFrom: event.target.value || undefined })}
                aria-label={t('Intervention from')}
                className="h-[38px] rounded-[9px] text-[13px]"
              />
              <Input
                type="date"
                value={filters.interventionTo ?? ''}
                onChange={(event) => onChange({ interventionTo: event.target.value || undefined })}
                aria-label={t('Intervention until')}
                className="h-[38px] rounded-[9px] text-[13px]"
              />
            </div>
          </Field>

          {/* A toggle and not a tri-state: only the bypasses are ever asked for. A `NULL` predates
              the column and says nothing about whether a send was compliant (D22 §3). */}
          <Field label={t('Perimeter')}>
            <label className="inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-[9px] border border-input px-[13px] text-[13px]">
              <input
                type="checkbox"
                checked={!!filters.outsidePerimeter}
                onChange={(event) =>
                  onChange({ outsidePerimeter: event.target.checked || undefined })
                }
                className="h-4 w-4 rounded border-input"
              />
              <ShieldAlert className="h-[15px] w-[15px] text-amber-600 dark:text-amber-500" aria-hidden="true" />
              {t('Sent outside the perimeter only')}
            </label>
          </Field>
        </div>
      )}
    </div>
  );
};
